import { SortDirection, SortDirectionType } from "@rio-cloud/rio-uikit";

import { TCategorizedByDate, TDateCategorizable, TDateCategoryConfig } from "./types";


/**
 * Default category configuration
 */
const DEFAULT_CATEGORY_CONFIG: TDateCategoryConfig = {
  today: 0,
  last7Days: 7,
  last30Days: 30,
};

/**
 * Categorizes a list of items by date ranges based on their timestamp property.
 * Items are sorted within each category according to the specified sort order.
 * The category order itself is also reversed when sorting ascending.
 *
 * @template T - Type of items to categorize, must have a timestamp property
 * @param {T[]} items - Array of items to categorize
 * @param {SortDirectionType} sortOrder - Sort order for items within categories
 * @param {Partial<TDateCategoryConfig>} [config] - Optional configuration for category thresholds
 * @returns {TCategorizedByDate<T>} Object containing items grouped by date ranges
 */
export const categorizeByDate = <T extends TDateCategorizable>(
  items: T[],
  sortOrder: SortDirectionType,
  config: Partial<TDateCategoryConfig> = {}
): TCategorizedByDate<T> => {
  const mergedConfig = { ...DEFAULT_CATEGORY_CONFIG, ...config };

  const today = new Date();
  const startOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const startOf7DaysAgo = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() - mergedConfig.last7Days
  );
  const startOf30DaysAgo = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() - mergedConfig.last30Days
  );

  const categorizedItems: TCategorizedByDate<T> = {
    today: [],
    last7Days: [],
    last30Days: [],
    older: [],
  };

  items.forEach((item) => {
    const itemDate = new Date(item.timestamp);

    if (itemDate >= startOfToday) {
      categorizedItems.today.push(item);
    } else if (itemDate >= startOf7DaysAgo) {
      categorizedItems.last7Days.push(item);
    } else if (itemDate >= startOf30DaysAgo) {
      categorizedItems.last30Days.push(item);
    } else {
      categorizedItems.older.push(item);
    }
  });

  const sortByTimestamp = (a: T, b: T): number =>
    sortOrder === SortDirection.DESCENDING
      ? new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      : new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();

  categorizedItems.today.sort(sortByTimestamp);
  categorizedItems.last7Days.sort(sortByTimestamp);
  categorizedItems.last30Days.sort(sortByTimestamp);
  categorizedItems.older.sort(sortByTimestamp);

  if (sortOrder === SortDirection.ASCENDING) {
    return {
      older: categorizedItems.older,
      last30Days: categorizedItems.last30Days,
      last7Days: categorizedItems.last7Days,
      today: categorizedItems.today,
    };
  }

  return {
    today: categorizedItems.today,
    last7Days: categorizedItems.last7Days,
    last30Days: categorizedItems.last30Days,
    older: categorizedItems.older,
  };
};